import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { mapToArray } from '@juvare/widget-ui/Utils';
import { WidgetComponent } from '@juvare/widget-ui/WidgetComponent';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import { Box, Button, FormControl, FormLabel, IconButton } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { DragTargetPlaceholder, FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getChildWidgetStyle, getLabelAndValuePairsFromChildDynamicRows, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { useCommonFormUiTranslation } from '../hooks';
import { icons } from '../icons/icons';
import { type } from './types';
const WidgetFormDataGrid = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const error = getVisibleError(props);
    const length = props.childDynamicRows ? props.childDynamicRows.length : 0;
    const minLength = (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.minLength;
    const maxLength = (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.maxLength;
    const allowRemove = minLength == null || length > minLength;
    const allowAdd = maxLength == null || length < maxLength;
    const allowReorder = props.config.addControls || props.config.reorder;
    const customIcons = (_c = props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.buttonIcons;
    const formLabelSuffix = getFormLabelSuffix(props);
    const RemoveIcon = customIcons ? customIcons['icon-remove'] : icons['icon-remove'];
    const readOnly = props.config.readOnly || ((_d = props.rootParams.formParams) === null || _d === void 0 ? void 0 : _d.readOnly);
    return (React.createElement(FormControl, { fullWidth: true, error: !!error },
        props.config.label && (React.createElement(FormLabel, { sx: { mb: 2 } }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(Box, { component: 'table', sx: {
                width: '100%',
                margin: 0,
                borderSpacing: 0,
                '& > tbody > tr > td': {
                    p: 0,
                    pr: 2,
                    pb: 2,
                    verticalAlign: 'top',
                },
                '& > tbody > tr > td:last-child': {
                    pr: 0,
                },
                '& > tbody > tr:last-child > td': {
                    pb: 0,
                },
            } },
            React.createElement("tbody", null,
                props.childDynamicRows &&
                    props.childDynamicRows.map((row) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        const currentRow = row.index + 1;
                        return (React.createElement("tr", { key: row.id },
                            row.childProps.map((childProps, index) => {
                                return (React.createElement(React.Fragment, { key: childProps.id },
                                    props.editMode && index === 0 && (React.createElement("td", { style: { paddingLeft: 0, paddingRight: 0 } },
                                        React.createElement("div", { "data-drag-target": true, "data-editor-path": 'children.#0+' },
                                            React.createElement("div", { "data-drag-target-container": true, style: { display: 'none', paddingRight: 8 } })))),
                                    childProps.hidden && !props.editMode ? (React.createElement("td", { style: getChildWidgetStyle(childProps), "data-editor-path": 'children.#' + index })) : (React.createElement("td", { style: getChildWidgetStyle(childProps), "data-editor-path": 'children.#' + index, "data-widget": childProps.dataPath, "data-visible-error": getVisibleError(childProps) ? 'true' : undefined },
                                        React.createElement(WidgetComponent, Object.assign({}, childProps, { renderData: props.renderData })))),
                                    props.editMode && (React.createElement("td", { style: { paddingLeft: 0, paddingRight: 0 } },
                                        React.createElement("div", { "data-drag-target": true, "data-editor-path": 'children.#' + (index + 1) + '+' },
                                            React.createElement("div", { "data-drag-target-container": true, style: { display: 'none', paddingRight: 8 } }))))));
                            }),
                            props.editMode && row.childProps.length === 0 && (React.createElement("td", null,
                                React.createElement("div", { "data-drag-target": true, "data-editor-path": 'children.#0+' },
                                    React.createElement("div", { "data-drag-target-container": true, style: { display: 'none' } }),
                                    React.createElement(DragTargetPlaceholder, null, "Drag and drop components from the toolbar here")))),
                            !readOnly && (allowRemove || allowReorder) && (React.createElement("td", { style: {
                                    width: (allowReorder ? 80 : 0) + (allowRemove ? 40 : 0),
                                    textAlign: 'right',
                                } },
                                allowReorder && (React.createElement(React.Fragment, null,
                                    row.index - 1 >= 0 && (React.createElement(IconButton, { onClick: () => {
                                            props.moveDataRow(row.index, row.index - 1);
                                            renderForm(props, {
                                                changed: true,
                                                autoSaveDebounce: props.config.autoSave,
                                            });
                                        }, disabled: (_a = props.config) === null || _a === void 0 ? void 0 : _a.disabled, tabIndex: props.editMode ? -1 : undefined, "data-test": 'up-button', "aria-label": `Move row ${currentRow} up` },
                                        React.createElement(ArrowUpwardIcon, { color: 'primary', sx: {
                                                color: ((_b = props.config) === null || _b === void 0 ? void 0 : _b.disabled) ? themeColors.icon.disable : undefined,
                                            } }))),
                                    row.index + 1 < ((_d = (_c = props.childDynamicRows) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) ? (React.createElement(IconButton, { onClick: () => {
                                            props.moveDataRow(row.index, row.index + 1);
                                            renderForm(props, {
                                                changed: true,
                                                autoSaveDebounce: props.config.autoSave,
                                            });
                                        }, tabIndex: props.editMode ? -1 : undefined, disabled: (_e = props.config) === null || _e === void 0 ? void 0 : _e.disabled, "data-test": 'down-button', "aria-label": `Move row ${currentRow} down` },
                                        React.createElement(ArrowDownwardIcon, { color: 'primary', sx: {
                                                color: ((_f = props.config) === null || _f === void 0 ? void 0 : _f.disabled) ? themeColors.icon.disable : undefined,
                                            } }))) : (React.createElement("span", { style: { paddingLeft: 40 } })))),
                                allowRemove && (React.createElement(IconButton, { onClick: () => {
                                        var _a;
                                        props.deleteDataRow(row.index);
                                        if (props.config.emptyAsNull && ((_a = props.childDynamicRows) === null || _a === void 0 ? void 0 : _a.length) === 0) {
                                            props.setData(undefined);
                                        }
                                        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                                    }, tabIndex: props.editMode ? -1 : undefined, disabled: (_g = props.config) === null || _g === void 0 ? void 0 : _g.disabled, "data-test": 'delete-button', "aria-label": `Remove row ${currentRow}` },
                                    React.createElement(RemoveIcon, { color: 'primary', sx: { color: ((_h = props.config) === null || _h === void 0 ? void 0 : _h.disabled) ? themeColors.icon.disable : undefined } })))))));
                    }),
                !readOnly && allowAdd && (React.createElement("tr", null,
                    React.createElement("td", { colSpan: props.config.children.length + (allowRemove ? 1 : 0) },
                        React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'text', onClick: () => {
                                props.insertDataRow();
                                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                            }, tabIndex: props.editMode ? -1 : undefined, disabled: (_e = props.config) === null || _e === void 0 ? void 0 : _e.disabled, "data-test": 'add-button', autoFocus: (_f = (!props.editMode && props.config.autoFocus)) !== null && _f !== void 0 ? _f : false }, (_g = props.config.addButtonLabel) !== null && _g !== void 0 ? _g : 'Add')))))),
        React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
};
const WidgetFormDataGridEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetDataGrid.dataGrid') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            readOnly: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'reorder',
                    label: t('widget.widgetDataGrid.allowReorder'),
                },
                {
                    type: 'form.checkbox',
                    key: 'disabled',
                    label: t('common.disabled'),
                },
                {
                    type: 'form.text',
                    key: 'addButtonLabel',
                    label: t('widget.widgetButton.addButtonLabel'),
                },
                {
                    type: 'form.checkbox',
                    key: 'emptyAsNull',
                    label: t('widget.widgetForm.emptyAsNull'),
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            minLength: true,
            maxLength: true,
        }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDataGrid, Object.assign({}, props))));
};
export const widgetFormDataGrid = {
    type,
    init: (props) => {
        var _a;
        let values = props.getData();
        if (values == null && !props.config.emptyAsNull) {
            values = [];
            props.setData(values);
        }
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.minLength) != null) {
            const array = mapToArray(values);
            for (let i = array.length; i < props.config.validate.minLength; i++) {
                props.insertDataRow();
            }
        }
    },
    validate: (props) => {
        var _a, _b, _c;
        if (isSkipValidate(props)) {
            return;
        }
        const values = props.getDataArray();
        if (props.config.validate) {
            if (props.config.validate.required) {
                if (values.length === 0) {
                    return (_a = props.config.validate.requiredMessage) !== null && _a !== void 0 ? _a : (props.config.label ? props.config.label + ' is required' : 'Required');
                }
            }
            if (props.config.validate.minLength) {
                if (values.length < props.config.validate.minLength) {
                    return ((_b = props.config.validate.minLengthMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' length cannot be less than ' : 'Length cannot be less than ') +
                        props.config.validate.minLength);
                }
            }
            if (props.config.validate.maxLength) {
                if (values.length > props.config.validate.maxLength) {
                    return ((_c = props.config.validate.maxLengthMessage) !== null && _c !== void 0 ? _c : (props.config.label ? props.config.label + ' length cannot be greater than ' : 'Length cannot be greater than ') +
                        props.config.validate.maxLength);
                }
            }
        }
        return validateCustom(props, values);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairsFromChildDynamicRows(props, joinLabels(labelPrefix, props.config.label));
    },
    childConfigs: (config) => config.children,
    childDynamicRows: true,
    component: WidgetFormDataGrid,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDataGridEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'dataGrid', label: 'Data Grid', children: [] }),
};
